// src/sdk.ts
var StratosSDK = class {
  constructor(config = {}) {
    this.connected = false;
    this.user = null;
    this.addresses = [];
    this.eventListeners = /* @__PURE__ */ new Map();
    this.pendingRequests = /* @__PURE__ */ new Map();
    this.parentOrigin = config.parentOrigin || this.detectParentOrigin();
    this.timeout = config.timeout || 3e4;
    this.debug = config.debug || false;
    if (window.parent !== window) {
      window.addEventListener("message", this.handleMessage.bind(this));
      this.log("StratosSDK initialized", { parentOrigin: this.parentOrigin });
    } else {
      this.log("Not in iframe, StratosSDK will not function");
    }
  }
  detectParentOrigin() {
    try {
      if (document.referrer) {
        return new URL(document.referrer).origin;
      }
    } catch {
    }
    return "*";
  }
  log(message, data) {
    if (this.debug) {
      console.log(`[StratosSDK] ${message}`, data || "");
    }
  }
  generateId() {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
  request(method, params) {
    return new Promise((resolve, reject) => {
      if (window.parent === window) {
        reject(new Error("Not in iframe"));
        return;
      }
      const id = this.generateId();
      const timeoutId = setTimeout(() => {
        this.pendingRequests.delete(id);
        reject(new Error(`Request timeout: ${method}`));
      }, this.timeout);
      this.pendingRequests.set(id, { resolve, reject, timeout: timeoutId });
      const message = { id, method, params };
      this.log("Sending request", message);
      window.parent.postMessage(message, this.parentOrigin);
    });
  }
  handleMessage(event) {
    if (this.parentOrigin !== "*" && event.origin !== this.parentOrigin) {
      return;
    }
    const data = event.data;
    if (data && typeof data.id === "string") {
      const response = data;
      const pending = this.pendingRequests.get(response.id);
      if (pending) {
        clearTimeout(pending.timeout);
        this.pendingRequests.delete(response.id);
        this.log("Received response", response);
        if (response.error) {
          pending.reject(new Error(response.error.message));
        } else {
          pending.resolve(response.result);
        }
      }
      return;
    }
    if (data && data.type === "event") {
      const eventMsg = data;
      this.log("Received event", eventMsg);
      this.handleEvent(eventMsg.event, eventMsg.data);
    }
  }
  handleEvent(event, data) {
    switch (event) {
      case "connect":
        const connState = data;
        this.connected = connState.connected;
        this.user = connState.user;
        this.addresses = connState.addresses;
        break;
      case "disconnect":
        this.connected = false;
        this.user = null;
        this.addresses = [];
        break;
      case "userChanged":
        this.user = data;
        break;
      case "addressesChanged":
        this.addresses = data;
        break;
    }
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(data);
        } catch (err) {
          console.error(`Error in ${event} listener:`, err);
        }
      });
    }
  }
  // ============================================
  // Connection
  // ============================================
  async connect() {
    const result = await this.request("connect");
    this.connected = result.connected;
    this.user = result.user;
    this.addresses = result.addresses;
    return result;
  }
  async disconnect() {
    await this.request("disconnect");
    this.connected = false;
    this.user = null;
    this.addresses = [];
  }
  isConnected() {
    return this.connected;
  }
  /**
   * Get the current network mode from the wallet
   * @returns 'mainnet' or 'testnet'
   */
  async getNetwork() {
    return this.request("getNetwork");
  }
  // ============================================
  // User & Addresses
  // ============================================
  async getUser() {
    if (this.user) return this.user;
    this.user = await this.request("getUser");
    return this.user;
  }
  async getAddresses() {
    if (this.addresses.length > 0) return this.addresses;
    this.addresses = await this.request("getAddresses");
    return this.addresses;
  }
  async getAddress(chainType) {
    const addresses = await this.getAddresses();
    const addr = addresses.find((a) => a.chainType === chainType);
    return addr?.address || null;
  }
  // ============================================
  // Assets & Balances
  // ============================================
  async getAssets() {
    return this.request("getAssets");
  }
  async getBalance(symbol, chainType) {
    const assets = await this.getAssets();
    const asset = assets.find((a) => {
      if (chainType) {
        return a.symbol === symbol && a.chainType === chainType;
      }
      return a.symbol === symbol;
    });
    return asset?.balance || 0;
  }
  // ============================================
  // Transactions
  // ============================================
  async getTransactions() {
    return this.request("getTransactions");
  }
  async transfer(params) {
    return this.request("transfer", params);
  }
  // ============================================
  // Signing
  // ============================================
  async signMessage(params) {
    return this.request("signMessage", params);
  }
  // ============================================
  // EVM Transactions
  // ============================================
  /**
   * Sign an EVM transaction without broadcasting
   * @param params Transaction parameters
   * @returns Signed transaction and hash
   */
  async signEVMTransaction(params) {
    return this.request("signEVMTransaction", params);
  }
  /**
   * Send an EVM transaction (sign and broadcast)
   * @param params Transaction parameters
   * @returns Transaction hash and status
   */
  async sendEVMTransaction(params) {
    return this.request("sendEVMTransaction", params);
  }
  /**
   * Sign EIP-712 typed data
   * @param params Typed data parameters
   * @returns Signature
   */
  async signTypedData(params) {
    return this.request("signTypedData", params);
  }
  /**
   * Convenience method to send ETH/native token
   * @param to Recipient address
   * @param value Amount in wei (as hex or decimal string)
   * @param chainId Chain ID
   * @returns Transaction result
   */
  async sendNative(to, value, chainId) {
    const tx = {
      to,
      value: value.startsWith("0x") ? value : "0x" + BigInt(value).toString(16),
      chainId
    };
    return this.sendEVMTransaction({ transaction: tx });
  }
  /**
   * Convenience method to call a contract function
   * @param to Contract address
   * @param data Encoded function call data
   * @param chainId Chain ID
   * @param value Optional value to send
   * @returns Transaction result
   */
  async sendContractCall(to, data, chainId, value) {
    const tx = {
      to,
      data,
      chainId,
      value: value ? value.startsWith("0x") ? value : "0x" + BigInt(value).toString(16) : "0x0"
    };
    return this.sendEVMTransaction({ transaction: tx });
  }
  // ============================================
  // Raw Solana Transactions (for DEX swaps like Jupiter)
  // ============================================
  /**
   * Sign a raw Solana transaction (e.g., from Jupiter aggregator)
   * @param params Transaction parameters including base64 encoded transaction
   * @returns Signed transaction and signature
   */
  async signRawSolanaTransaction(params) {
    return this.request("signRawSolanaTransaction", params);
  }
  /**
   * Send a raw Solana transaction (sign + broadcast)
   * @param params Signed transaction to broadcast
   * @returns Transaction signature and status
   */
  async sendRawSolanaTransaction(params) {
    return this.request("sendRawSolanaTransaction", params);
  }
  // ============================================
  // Raw TON Messages (for DEX swaps like STON.fi)
  // ============================================
  /**
   * Sign a raw TON message (e.g., for STON.fi swap)
   * @param params Message parameters including payload
   * @returns BOC encoded signed message and hash
   */
  async signRawTonMessage(params) {
    return this.request("signRawTonMessage", params);
  }
  /**
   * Send a raw TON message (broadcast signed message)
   * @param params BOC encoded signed message
   * @returns Message hash and status
   */
  async sendRawTonMessage(params) {
    return this.request("sendRawTonMessage", params);
  }
  // ============================================
  // TRON Smart Contract (for DEX swaps like SunSwap)
  // ============================================
  /**
   * Trigger a TRON smart contract (e.g., for SunSwap)
   * @param params Contract call parameters
   * @returns Signed transaction result
   */
  async triggerTronSmartContract(params) {
    return this.request("triggerTronSmartContract", params);
  }
  /**
   * Broadcast a signed TRON transaction
   * @param params Signed transaction to broadcast
   * @returns Transaction ID and status
   */
  async broadcastTronTransaction(params) {
    return this.request("broadcastTronTransaction", params);
  }
  /**
   * Get an EVM transaction receipt (used to poll for contract deployment address)
   * @param txHash Transaction hash
   * @param chainId Chain ID
   * @returns Transaction receipt
   */
  async getTransactionReceipt(txHash, chainId) {
    return this.request("getTransactionReceipt", { txHash, chainId });
  }
  /**
   * Read-only EVM RPC call (eth_call, eth_getBalance, etc.)
   * Routed through the portal to the appropriate RPC endpoint.
   * @param params RPC method, params, and chainId
   * @returns Raw RPC result string
   */
  async evmCall(params) {
    return this.request("evmCall", params);
  }
  /**
   * Get portal configuration values relevant to mini-apps
   * Returns config keys like usdcTemplateId, etc.
   */
  async getConfig() {
    return this.request("getConfig");
  }
  /**
   * Accept pending Splice TransferOffer contracts for the bridge operator.
   * Used by auto-accept to process incoming CC transfers to the bridge.
   */
  async acceptBridgeTransferOffers() {
    return this.request("acceptBridgeOffers");
  }
  /**
   * Create a Splice TransferOffer from BridgeOperator to a user (CC release).
   * Used in the EVM→CC bridge flow after the bridge processes the request.
   * @param params Amount and recipient party for the CC release
   * @returns The created contract ID
   */
  async bridgeReleaseCC(params) {
    return this.request("bridgeReleaseCC", params);
  }
  /**
   * Transfer USDCHolding from BridgeOperator to a user on Canton.
   * Used in the CC→USDC (Canton same-chain) swap flow.
   * @param params Amount and recipient party for the USDC release
   * @returns The transferred contract ID
   */
  async bridgeReleaseUSDC(params) {
    return this.request("bridgeReleaseUSDC", params);
  }
  /**
   * Generic API proxy — call any backend endpoint through the portal.
   * Allows mini-apps to use backend APIs without adding SDK methods per endpoint.
   * @param path API path (e.g. '/api/canton/bridge-release-cc')
   * @param body JSON body to POST
   * @returns The response data (parsed JSON)
   */
  async callAPI(path, body) {
    return this.request("callAPI", { path, body: body || {} });
  }
  // ============================================
  // Canton-Specific
  // ============================================
  async getPartyId() {
    const user = await this.getUser();
    return user?.partyId || null;
  }
  async getTransferOffers() {
    return this.request("getTransferOffers");
  }
  /**
   * Accept all pending transfer offers for the current user.
   * Used at the end of swap flows to ensure CC lands in the wallet.
   */
  async acceptAllOffers() {
    return this.request("acceptAllOffers");
  }
  async acceptTransferOffer(contractId) {
    return this.request("acceptOffer", { contractId });
  }
  // ============================================
  // Canton Generic Contract Operations
  // ============================================
  /**
   * Query Canton contracts by template ID
   * @param params Query parameters including templateId and optional filter
   * @returns Array of matching contracts
   */
  async cantonQuery(params) {
    return this.request("cantonQuery", params);
  }
  /**
   * Create a new Canton contract
   * @param params Create parameters including templateId and payload
   * @returns The created contract ID
   */
  async cantonCreate(params) {
    return this.request("cantonCreate", params);
  }
  /**
   * Exercise a choice on a Canton contract
   * @param params Exercise parameters including contractId, choice name, and arguments
   * @returns The exercise result
   */
  async cantonExercise(params) {
    return this.request("cantonExercise", params);
  }
  /**
   * Grant user rights on the Canton ledger
   * Used to grant readAs/actAs rights to users (e.g., readAs public party during onboarding)
   * @param params Grant parameters including userId and rights to grant
   * @returns The grant result
   */
  async grantUserRights(params) {
    return this.request("grantUserRights", params);
  }
  // ============================================
  // Data Refresh
  // ============================================
  async refresh() {
    await this.request("refresh");
  }
  // ============================================
  // Events
  // ============================================
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, /* @__PURE__ */ new Set());
    }
    this.eventListeners.get(event).add(callback);
  }
  off(event, callback) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }
  removeAllListeners() {
    this.eventListeners.clear();
  }
  // ============================================
  // Cleanup
  // ============================================
  destroy() {
    window.removeEventListener("message", this.handleMessage.bind(this));
    this.pendingRequests.forEach(({ timeout }) => clearTimeout(timeout));
    this.pendingRequests.clear();
    this.eventListeners.clear();
  }
};
var defaultInstance = null;
function getSDK(config) {
  if (!defaultInstance) {
    defaultInstance = new StratosSDK(config);
  }
  return defaultInstance;
}

// src/parentBridge.ts
var ParentBridge = class {
  constructor(callbacks, config) {
    this.iframeOrigins = /* @__PURE__ */ new Map();
    this.callbacks = callbacks;
    this.allowedOrigins = new Set(config.allowedOrigins);
    this.debug = config.debug || false;
    window.addEventListener("message", this.handleMessage.bind(this));
    this.log("ParentBridge initialized", { allowedOrigins: config.allowedOrigins });
  }
  log(message, data) {
    if (this.debug) {
      console.log(`[ParentBridge] ${message}`, data || "");
    }
  }
  isAllowedOrigin(origin) {
    if (origin.startsWith("http://localhost:") || origin.startsWith("http://127.0.0.1:")) {
      return true;
    }
    return this.allowedOrigins.has(origin);
  }
  async handleMessage(event) {
    if (!this.isAllowedOrigin(event.origin)) {
      this.log("Rejected message from untrusted origin", event.origin);
      return;
    }
    const data = event.data;
    if (!data || typeof data.id !== "string" || typeof data.method !== "string") {
      return;
    }
    this.log("Received request", { method: data.method, params: data.params });
    if (event.source) {
      this.iframeOrigins.set(event.origin, event.source);
    }
    try {
      const result = await this.processRequest(data.method, data.params);
      this.sendResponse(event.source, event.origin, {
        id: data.id,
        result
      });
    } catch (error) {
      this.sendResponse(event.source, event.origin, {
        id: data.id,
        error: {
          code: -1,
          message: error instanceof Error ? error.message : "Unknown error"
        }
      });
    }
  }
  async processRequest(method, params) {
    switch (method) {
      case "connect": {
        const user = this.callbacks.getUser();
        const addresses = this.callbacks.getAddresses();
        return {
          connected: user !== null,
          user,
          addresses
        };
      }
      case "disconnect":
        return { success: true };
      case "getUser":
        return this.callbacks.getUser();
      case "getAddresses":
        return this.callbacks.getAddresses();
      case "getAssets":
        return this.callbacks.getAssets();
      case "getTransactions":
        return this.callbacks.getTransactions();
      case "getTransferOffers":
        return this.callbacks.getTransferOffers();
      case "transfer": {
        const transferParams = params;
        return this.callbacks.transfer(transferParams);
      }
      case "signMessage": {
        const signParams = params;
        return this.callbacks.signMessage(signParams);
      }
      case "acceptOffer": {
        const { contractId } = params;
        return this.callbacks.acceptOffer(contractId);
      }
      case "refresh":
        await this.callbacks.refresh();
        return { success: true };
      // Canton Generic Contract Operations
      case "cantonQuery": {
        if (!this.callbacks.cantonQuery) {
          throw new Error("Canton query not supported");
        }
        const queryParams = params;
        return this.callbacks.cantonQuery(queryParams);
      }
      case "cantonCreate": {
        if (!this.callbacks.cantonCreate) {
          throw new Error("Canton create not supported");
        }
        const createParams = params;
        return this.callbacks.cantonCreate(createParams);
      }
      case "cantonExercise": {
        if (!this.callbacks.cantonExercise) {
          throw new Error("Canton exercise not supported");
        }
        const exerciseParams = params;
        return this.callbacks.cantonExercise(exerciseParams);
      }
      // EVM Transaction Operations
      case "signEVMTransaction": {
        if (!this.callbacks.signEVMTransaction) {
          throw new Error("EVM transaction signing not supported");
        }
        const signTxParams = params;
        return this.callbacks.signEVMTransaction(signTxParams);
      }
      case "sendEVMTransaction": {
        if (!this.callbacks.sendEVMTransaction) {
          throw new Error("EVM transaction sending not supported");
        }
        const sendTxParams = params;
        return this.callbacks.sendEVMTransaction(sendTxParams);
      }
      case "signTypedData": {
        if (!this.callbacks.signTypedData) {
          throw new Error("EIP-712 typed data signing not supported");
        }
        const typedDataParams = params;
        return this.callbacks.signTypedData(typedDataParams);
      }
      // Canton User Rights
      case "grantUserRights": {
        if (!this.callbacks.grantUserRights) {
          throw new Error("User rights granting not supported");
        }
        const grantParams = params;
        return this.callbacks.grantUserRights(grantParams);
      }
      default:
        throw new Error(`Unknown method: ${method}`);
    }
  }
  sendResponse(target, origin, response) {
    this.log("Sending response", response);
    target.postMessage(response, origin);
  }
  /**
   * Broadcast an event to all connected iframes
   */
  broadcastEvent(event, data) {
    const message = {
      type: "event",
      event,
      data
    };
    this.log("Broadcasting event", { event, data });
    this.iframeOrigins.forEach((target, origin) => {
      try {
        target.postMessage(message, origin);
      } catch (err) {
        this.log("Failed to send event to iframe", { origin, error: err });
        this.iframeOrigins.delete(origin);
      }
    });
  }
  /**
   * Notify iframes that assets have changed
   */
  notifyAssetsChanged() {
    const assets = this.callbacks.getAssets();
    this.broadcastEvent("assetsChanged", assets);
  }
  /**
   * Notify iframes that transactions have changed
   */
  notifyTransactionsChanged() {
    const transactions = this.callbacks.getTransactions();
    this.broadcastEvent("transactionsChanged", transactions);
  }
  /**
   * Notify iframes that user has changed
   */
  notifyUserChanged() {
    const user = this.callbacks.getUser();
    this.broadcastEvent("userChanged", user);
  }
  /**
   * Notify iframes that addresses have changed
   */
  notifyAddressesChanged() {
    const addresses = this.callbacks.getAddresses();
    this.broadcastEvent("addressesChanged", addresses);
  }
  /**
   * Add an allowed origin dynamically
   */
  addAllowedOrigin(origin) {
    this.allowedOrigins.add(origin);
  }
  /**
   * Remove an allowed origin
   */
  removeAllowedOrigin(origin) {
    this.allowedOrigins.delete(origin);
    this.iframeOrigins.delete(origin);
  }
  /**
   * Cleanup
   */
  destroy() {
    window.removeEventListener("message", this.handleMessage.bind(this));
    this.iframeOrigins.clear();
  }
};

// src/types.ts
var EVM_CHAINS = {
  /** Ethereum Mainnet */
  ETHEREUM: 1,
  /** Ethereum Sepolia (testnet) */
  SEPOLIA: 11155111,
  /** Base */
  BASE: 8453
};
export {
  EVM_CHAINS,
  ParentBridge,
  StratosSDK,
  getSDK
};
